import { useEffect, useState } from 'react';
import { IQuestion, IQuestionOption } from 'types/forms';
import useModal from 'hooks/useModal';
import { createOptions } from './constants';

const convertOptions = (options: unknown[]): IQuestionOption[] => {
  if (typeof options[0] === 'string') {
    return options.map((item, idx) => ({ id: idx + 1, value: item as string }));
  }

  return options as IQuestionOption[];
};

const nextId = (questions: IQuestion[]) =>
  Math.max(0, ...questions.map((item) => item.id)) + 1;

const createQuestion = (id: number): IQuestion => ({
  id,
  type: 'SINGLE_CHOICE',
  title: '',
  description: '',
  options: createOptions('SINGLE_CHOICE'),
});

interface Props {
  formData: { title?: string; questions?: IQuestion[] };
  onSave: (title: string, questions: IQuestion[]) => void;
}

function useForms({ formData, onSave }: Props) {
  const { handleAlert } = useModal();
  const [title, setTitle] = useState(formData.title || '');
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [focusId, setFocusId] = useState<number | null>(null);

  const load = (title?: string, questions?: IQuestion[]) => {
    const data: IQuestion[] = questions?.length
      ? questions.map((q) => ({
          ...q,
          options: convertOptions(q.options || []),
        }))
      : [createQuestion(1)];

    setTitle(title || '');
    setQuestions(data);
  };

  const handleAddQuestion = () => {
    const id = nextId(questions);
    setQuestions([...questions, createQuestion(id)]);
    setFocusId(id);
  };

  const handleDuplicateQuestion = (id: number) => {
    const idx = questions.findIndex((item) => item.id === id);
    if (idx === -1) return;

    const newId = nextId(questions);
    const source = questions[idx];
    const copy: IQuestion = {
      ...source,
      id: newId,
      options: source.options?.map((o) => ({ ...o })),
    };
    setQuestions([
      ...questions.slice(0, idx + 1),
      copy,
      ...questions.slice(idx + 1),
    ]);
    setFocusId(newId);
  };

  const handleRemoveQuestion = (id: number) => {
    if (questions.length === 1) {
      handleAlert('질문은 최소 1개 이상이어야 합니다.');
      return;
    }

    setQuestions(questions.filter((item) => item.id !== id));
  };

  const handleChangeQuestion = (question: IQuestion) => {
    setQuestions((prev) =>
      prev.map((item) => (item.id === question.id ? question : item)),
    );
  };

  const handleMoveQuestion = (from: number, to: number) => {
    setQuestions((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleSave = () => {
    if (!title.trim()) {
      handleAlert('제목을 입력해주세요.');
      return;
    }

    const emptyIdx = questions.findIndex((q) => !q.title.trim());
    if (emptyIdx !== -1) {
      handleAlert(`${emptyIdx + 1}번 질문의 제목을 입력해주세요.`);
      return;
    }

    const noOptionIdx = questions.findIndex(
      (q) => q.type !== 'TEXT' && !q.options?.length,
    );
    if (noOptionIdx !== -1) {
      handleAlert(`${noOptionIdx + 1}번 질문에 옵션을 추가해주세요.`);
      return;
    }

    onSave(title.trim(), questions);
  };

  useEffect(() => {
    load(formData.title, formData.questions);
  }, [formData.title, formData.questions]);

  return {
    title,
    setTitle,
    questions,
    focusId,
    handleAddQuestion,
    handleDuplicateQuestion,
    handleRemoveQuestion,
    handleMoveQuestion,
    handleSave,
    load,
    handleChangeQuestion,
  };
}

export default useForms;
